import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUnits } from 'shared/slice/unitsSlice';
import {
  convertFahrenheitToCelsius,
  formatTime,
  isAnotherSystemRunning,
} from 'shared/helpers/helpers';
import {
  createScheduleService,
  deleteScheduleService,
  updateScheduleService,
} from 'shared/services/schedule.service';
import { CONTROL_BOX_CONFIG } from './controlBoxConfig';

export const useControlHandlers = ({ swtName, location, machine }) => {
  const config = CONTROL_BOX_CONFIG[swtName];
  const { postCommand, actions, deviceType, conflictCheckTarget, conflictStrings } = config;

  const dispatch = useDispatch();
  const switchData = useSelector(config.switchSelector);
  const flatSwitch = switchData[config.flatSwitchKey];
  const selectedSwitch = flatSwitch?.[location]?.[machine];
  const switchStatus = selectedSwitch || {};
  const {
    deviceMac,
    heatingScheduleList = [],
    heatingSchedule = {},
    instantHeat = {},
    snowSensor = {},
    windFactor = {},
    currentRun,
  } = switchStatus;

  const { isF } = useSelector(selectUnits);

  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [message, setMessage] = useState([]);
  const [programName, setProgramName] = useState(null);
  const [editingProgramKey, setEditingProgramKey] = useState(null);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);

  useEffect(() => {
    if (!actions.readyHeatingSchedule || !selectedSwitch) return;

    const hasTemp = heatingScheduleList?.[0]?.inputTemp > 0;
    dispatch(actions.readyHeatingSchedule({ location, machine, state: hasTemp }));
  }, [actions, dispatch, heatingScheduleList, location, machine, selectedSwitch]);

  const handleMessageBox = (id, program) => {
    switch (id) {
      case 'tempA':
        setMessage([
          `in order to finalize ${program} `,
          'please input your temperature',
          '( the minimum temperature is 121°C - 250°F )',
          '( the maximum temperature is 999°C - 1830°F )',
        ]);
        setOpenMessageBox(true);
        break;
      case 'tempB':
        setMessage([
          `in order to finalize ${program}, `,
          'please input your temperature',
          '( the minimum temperature is 25°C - 77°F )',
          '( the maximum temperature is 120°C - 248°F )',
        ]);
        setOpenMessageBox(true);
        break;
      case 'selectA':
        setMessage(['please select locations first']);
        setOpenMessageBox(true);
        break;
      case 'selectB':
        setMessage(['please select locations and a schedule first']);
        setOpenMessageBox(true);
        break;
      case 'fanOnly':
        setMessage([
          'the fan should always run while the heater is running in tgs',
        ]);
        setOpenMessageBox(true);
        break;
      default:
        throw new Error(`unknown message type: ${id}`);
    }
  };

  const checkConflict = (commandTarget, extraData) => {
    if (!conflictCheckTarget || !isAnotherSystemRunning(currentRun, conflictCheckTarget)) {
      return false;
    }
    dispatch(actions.activateConflictMessage({ location, machine }));
    dispatch(
      actions.setDevicesConflicts({
        location,
        machine,
        currentSwitch: conflictStrings.currentSwitch,
        DesiredSwitch: conflictStrings.desiredSwitch,
        systemTarget: conflictStrings.systemTarget,
        commandTarget,
        extraData,
      })
    );
    return true;
  };

  const instantHeatHandler = (state, temp) => {
    if (state === 'on') {
      const commandTemp = isF ? convertFahrenheitToCelsius(temp) : temp;
      if (checkConflict('on_switch', { commandTemp, temp })) return;
      postCommand(deviceMac, 'instant_temp', commandTemp);
      postCommand(deviceMac, 'on_switch', 1);
      dispatch(actions.instantHeatReady({ location, machine, isF, temp }));
    } else if (state === 'off') {
      postCommand(deviceMac, 'on_switch', 0);
      dispatch(actions.instantHeatOff({ location, machine }));
    } else {
      setProgramName('instant heat');
      setEditingProgramKey('instantHeat');
      handleMessageBox(state, 'instant heat');
    }
  };

  const constantTempHandler = (state, temp) => {
    if (!actions.optionalConstantTempReady) return;
    if (state === 'on') {
      const commandTemp = isF ? convertFahrenheitToCelsius(temp) : temp;
      if (checkConflict('on_constant', { commandTemp, temp })) return;
      postCommand(deviceMac, 'constant_temp', commandTemp);
      postCommand(deviceMac, 'on_constant', 1);
      dispatch(actions.optionalConstantTempReady({ location, machine, isF, temp }));
    } else if (state === 'off') {
      postCommand(deviceMac, 'on_constant', 0);
      dispatch(actions.optionalConstantTempOff({ location, machine }));
    } else {
      setProgramName('optional constant temp.');
      setEditingProgramKey('optionalConstantTemp');
      handleMessageBox(state, 'optional constant temp.');
    }
  };

  const snowSensorHandler = (state) => {
    if (state === 'off') {
      postCommand(deviceMac, 'snow_enabled', 0);
      dispatch(actions.snowSensorOff({ location, machine }));
    } else if (state === 'on') {
      if (checkConflict('snow_enabled')) return;
      postCommand(deviceMac, 'snow_enabled', 1);
      dispatch(actions.snowSensor({ location, machine }));
    } else {
      setProgramName('snow sensor program');
      setEditingProgramKey(null);
      handleMessageBox(state, 'snow sensor program');
    }
  };

  const windFactorHandler = (state) => {
    if (state === 'off') {
      postCommand(deviceMac, 'wind', 0);
      dispatch(actions.windFactorOff({ location, machine }));
    } else {
      if (checkConflict('wind')) return;
      postCommand(deviceMac, 'wind', 1);
      dispatch(actions.windFactor({ location, machine }));
    }
  };

  const fanOnlyHandler = (state) => {
    if (!actions.fanOnly) return;
    if (state === 'on') {
      postCommand(deviceMac, 'fan', 1);
      dispatch(actions.fanOnly({ location, machine, state: true }));
    } else {
      if (
        instantHeat.isActivated ||
        snowSensor.isActivated ||
        windFactor.isActivated ||
        heatingSchedule.isActivated
      ) {
        handleMessageBox('fanOnly');
      } else {
        postCommand(deviceMac, 'fan', 0);
        dispatch(actions.fanOnly({ location, machine, state: false }));
      }
    }
  };

  const heatingScheduleHandler = (state, temp, data, index) => {
    if (state === 'selectB') {
      setShowScheduleEditor(true);
      return;
    }
    if (state === 'off') {
      const emptyData = [
        { start: { date: null, time: null }, end: { date: null, time: null }, inputTemp: null, isF: null },
      ];
      dispatch(actions.clearHeatingSchedule({ location, machine, data: emptyData }));
    } else if (state === 'clear') {
      const emptyData = [
        { start: { date: null, time: null }, end: { date: null, time: null }, inputTemp: null, isF: null, id: null },
      ];
      if (heatingSchedule.length === 2) {
        deleteScheduleService(heatingScheduleList[index]?.id)
          .then(() => dispatch(actions.clearHeatingSchedule({ location, machine, data: emptyData })))
          .catch((err) => console.error('delete heating schedule fail:', err));
      } else if (heatingScheduleList.length - 1 !== index) {
        const filteredData = heatingScheduleList.filter((_, idx) => idx !== index);
        deleteScheduleService(heatingScheduleList[index]?.id)
          .then(() => dispatch(actions.clearHeatingSchedule({ location, machine, data: filteredData })))
          .catch((err) => console.error('delete heating schedule fail:', err));
      }
    } else {
      const startDate = formatTime({ ...data.start });
      const endDate = formatTime({ ...data.end });
      const threshold = isF ? convertFahrenheitToCelsius(temp) : temp;
      const scheduleData = { deviceId: deviceMac, deviceType, startDate, endDate, threshold };

      if (checkConflict('schedule', { start: data.start, end: data.end, temp, index })) return;

      const dispatchSchedule = (res) =>
        dispatch(
          actions.addHeatingSchedule({
            location,
            machine,
            start: data.start,
            end: data.end,
            index,
            inputTemp: temp,
            isF,
            id: res.id,
          })
        );

      if (heatingScheduleList[index]?.id) {
        updateScheduleService(heatingScheduleList[index].id, scheduleData)
          .then(dispatchSchedule)
          .catch((e) => console.error('update heating schedule fail:', e.message));
      } else {
        createScheduleService(scheduleData)
          .then(dispatchSchedule)
          .catch((e) => console.error('create heating schedule fail:', e.message));
      }
    }
  };

  const integratedButtonHandler = (id, state, temp, data, index) => {
    const selectedSwitch = flatSwitch?.[location]?.[machine];
    const ebpLocked = selectedSwitch?.EBP_mode === 0 && selectedSwitch?.isEbp;
    if (ebpLocked) return;

    switch (id) {
      case 'instantHeat':
        instantHeatHandler(state, temp);
        break;
      case 'snowSensor':
        snowSensorHandler(state);
        break;
      case 'constantTemp':
        constantTempHandler(state, temp);
        break;
      case 'windFactor':
        windFactorHandler(state);
        break;
      case 'heatingSchedule':
        heatingScheduleHandler(state, temp, data, index);
        break;
      case 'fanOnly':
        fanOnlyHandler(state);
        break;
      default:
        break;
    }
  };

  const handleCancelConflictMessage = () => {
    if (actions.deactivateConflictMessage) {
      dispatch(actions.deactivateConflictMessage({ location, machine }));
    }
  };

  const handleConfirmConflictMessage = (devicesConflicts) => {
    if (devicesConflicts.commandTarget === 'on_switch') {
      const commandTemp =
        typeof devicesConflicts.extraData === 'object' && devicesConflicts.extraData !== null
          ? devicesConflicts.extraData.commandTemp
          : devicesConflicts.extraData;
      const displayTemp =
        typeof devicesConflicts.extraData === 'object' && devicesConflicts.extraData !== null
          ? devicesConflicts.extraData.temp
          : devicesConflicts.extraData;
      postCommand(deviceMac, 'instant_temp', commandTemp);
      postCommand(deviceMac, 'on_switch', 1);
      dispatch(actions.instantHeatReady({ location, machine, isF, temp: displayTemp }));
    }
    if (devicesConflicts.commandTarget === 'on_constant' && actions.optionalConstantTempReady) {
      const commandTemp =
        typeof devicesConflicts.extraData === 'object' && devicesConflicts.extraData !== null
          ? devicesConflicts.extraData.commandTemp
          : devicesConflicts.extraData;
      const displayTemp =
        typeof devicesConflicts.extraData === 'object' && devicesConflicts.extraData !== null
          ? devicesConflicts.extraData.temp
          : devicesConflicts.extraData;
      postCommand(deviceMac, 'constant_temp', commandTemp);
      postCommand(deviceMac, 'on_constant', 1);
      dispatch(actions.optionalConstantTempReady({ location, machine, isF, temp: displayTemp }));
    }
    if (devicesConflicts.commandTarget === 'fan' && actions.fanOnly) {
      postCommand(deviceMac, 'fan', 1);
      dispatch(actions.fanOnly({ location, machine, state: true }));
    }
    if (devicesConflicts.commandTarget === 'snow_enabled') {
      postCommand(deviceMac, 'snow_enabled', 1);
      dispatch(actions.snowSensor({ location, machine }));
    }
    if (devicesConflicts.commandTarget === 'wind') {
      postCommand(deviceMac, 'wind', 1);
      dispatch(actions.windFactor({ location, machine }));
    }
    if (devicesConflicts.commandTarget === 'schedule') {
      const startDate = formatTime({ ...devicesConflicts.extraData.start });
      const endDate = formatTime({ ...devicesConflicts.extraData.end });
      const threshold = +devicesConflicts.extraData.temp;
      const scheduleData = { deviceId: deviceMac, deviceType, startDate, endDate, threshold };
      const dispatchData = (res) =>
        dispatch(
          actions.addHeatingSchedule({
            location,
            machine,
            start: devicesConflicts.extraData.start,
            end: devicesConflicts.extraData.end,
            index: devicesConflicts.extraData.index,
            inputTemp: devicesConflicts.extraData.temp,
            isF,
            id: res.id,
          })
        );
      if (heatingScheduleList[devicesConflicts.extraData.index]?.id) {
        updateScheduleService(heatingScheduleList[devicesConflicts.extraData.index].id, scheduleData)
          .then(dispatchData)
          .catch((e) => console.error('update heating schedule fail:', e.message));
      } else {
        createScheduleService(scheduleData)
          .then(dispatchData)
          .catch((e) => console.error('create heating schedule fail:', e.message));
      }
    }
    if (actions.deactivateConflictMessage) {
      dispatch(actions.deactivateConflictMessage({ location, machine }));
    }
  };

  return {
    integratedButtonHandler,
    openMessageBox,
    setOpenMessageBox,
    message,
    programName,
    editingProgramKey,
    showScheduleEditor,
    setShowScheduleEditor,
    handleCancelConflictMessage,
    handleConfirmConflictMessage,
  };
};
